import { useEffect, useState } from "react";
import { AlertTriangle, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveCompany } from "../../context/ActiveCompanyContext";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import { MONTHS, fmtSEK } from "../../components/planning/revenue/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const STATUS_STYLE = {
  DRAFT: "bg-[#FEF3C7] text-[#92400E]",
  ACTIVE: "bg-[#DCFCE7] text-[#166534]",
  CANCELLED: "bg-slate-200 text-slate-500",
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("sv-SE") : "—");

function StatusBadge({ status, testId }) {
  return (
    <span className={`inline-flex items-center rounded-full text-[10px] font-bold px-2 py-0.5 ${STATUS_STYLE[status] || ""}`} data-testid={testId}>
      {status}
    </span>
  );
}

function SummaryCell({ label, value, testId, strong }) {
  return (
    <div className="border border-slate-100 rounded-lg px-3 py-2" data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-sm mt-0.5 ${strong ? "font-bold text-[#0052CC]" : "font-semibold text-slate-900"}`}>{value}</div>
    </div>
  );
}

// Build 9B — Planning → Dividends. Management simulation per legal entity.
// Source fiscal year → financial basis; dividend decision date → ownership
// allocation; installments → cash-flow timing only.
export default function DividendsPage() {
  const { user, isAdmin } = useAuth();
  const canEdit = isAdmin || user?.role === "COMPANY_MANAGER";
  const { companyId, setCompanyId } = useActiveCompany();
  const [companies, setCompanies] = useState([]);
  const [years, setYears] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null); // full detail incl. computed
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ source_fiscal_year: "", payment_year: "", payout_percentage: "60", dividend_decision_date: "", notes: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ payout_percentage: "", dividend_decision_date: "", notes: "", override_amount: "", override_comment: "" });
  const [instDraft, setInstDraft] = useState([]);
  const [instMonth, setInstMonth] = useState("5");
  const [instAmount, setInstAmount] = useState("");
  const [confirmAction, setConfirmAction] = useState(null); // "activate" | "cancel" | "reopen"
  const [cancelComment, setCancelComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/companies")
      .then((c) => {
        setCompanies(c.data);
        if (c.data.length > 0 && !c.data.some((co) => co.id === companyId)) setCompanyId(c.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    setSelected(null);
    api.get(`/companies/${companyId}/periods/years`)
      .then((res) => setYears(res.data))
      .catch(() => setYears([]));
  }, [companyId]);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.get("/dividends", { params: { company_id: companyId } });
      setPlans(res.data);
      if (selected) {
        const fresh = res.data.find((p) => p.id === selected.id);
        setSelected(fresh || null);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (plan) => {
    try {
      const res = await api.get(`/dividends/${plan.id}`);
      setSelected(res.data);
      setInstDraft((res.data.installments || []).map((i) => ({ ...i })));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createPlan = async () => {
    setBusy(true);
    try {
      const res = await api.post("/dividends", {
        company_id: companyId,
        source_fiscal_year: Number(createForm.source_fiscal_year),
        payment_year: Number(createForm.payment_year),
        payout_percentage: Number(createForm.payout_percentage),
        dividend_decision_date: createForm.dividend_decision_date || null,
        notes: createForm.notes || null,
      });
      toast.success("Dividend plan created (DRAFT)");
      setCreateOpen(false);
      await load();
      await openDetail(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = {
        payout_percentage: Number(editForm.payout_percentage),
        dividend_decision_date: editForm.dividend_decision_date || null,
        notes: editForm.notes || null,
      };
      if (editForm.override_amount !== "") {
        payload.override_amount = Number(editForm.override_amount);
        payload.override_comment = editForm.override_comment || null;
      } else if (selected.override_amount != null) {
        payload.override_amount = null; // clear override
      }
      const res = await api.patch(`/dividends/${selected.id}`, payload);
      toast.success("Dividend plan updated");
      setEditOpen(false);
      setSelected(res.data);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveInstallments = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/dividends/${selected.id}/installments`, {
        installments: instDraft.map((i) => ({ payment_year: i.payment_year, payment_month: i.payment_month, amount: Number(i.amount) })),
      });
      toast.success("Payment schedule saved");
      setSelected(res.data);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runAction = async () => {
    setBusy(true);
    try {
      const id = selected.id;
      if (confirmAction === "activate") {
        await api.post(`/dividends/${id}/activate`);
        toast.success("Dividend plan ACTIVATED — installments now feed the official Cash Flow forecast");
      } else if (confirmAction === "cancel") {
        await api.post(`/dividends/${id}/cancel`, { comment: cancelComment });
        toast.success("Dividend plan cancelled (history retained)");
      } else if (confirmAction === "reopen") {
        await api.post(`/dividends/${id}/reopen`);
        toast.success("Dividend plan reopened as DRAFT");
      }
      setConfirmAction(null);
      setCancelComment("");
      await load();
      await openDetail({ id });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const c = selected?.computed;
  const snap = selected?.activation_snapshot;
  const editableSchedule = selected && selected.status !== "CANCELLED" && canEdit;

  return (
    <div data-testid="dividends-page">
      <PageHeader
        title="Dividends"
        subtitle="Dividend Planning is a management simulation. Final distributable amount is subject to approved annual accounts and formal dividend decision."
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="dividend-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((co) => (
                  <SelectItem key={co.id} value={co.id} data-testid={`dividend-company-${co.id}`}>{co.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canEdit && (
              <Button
                className="bg-[#0052CC] hover:bg-[#0047B3]"
                onClick={() => {
                  const src = years.length ? Math.max(...years) : new Date().getFullYear() - 1;
                  setCreateForm({ source_fiscal_year: String(src), payment_year: String(src + 1), payout_percentage: "60", dividend_decision_date: "", notes: "" });
                  setCreateOpen(true);
                }}
                data-testid="dividend-new-button"
              >
                <Plus className="h-4 w-4 mr-1" /> New dividend plan
              </Button>
            )}
          </div>
        }
      />

      {plans.length === 0 && !loading && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="dividend-empty">
          <EmptyState title="No dividend plans" description="Create a plan to simulate a dividend from a fiscal year's result." testId="dividend-empty-state" />
        </div>
      )}

      {plans.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-6" data-testid="dividend-list">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">Plan</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4">Result basis</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4">Payout</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4">Planned dividend</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr
                  key={p.id}
                  className={`border-b border-slate-100 cursor-pointer hover:bg-accent/40 transition-colors ${selected?.id === p.id ? "bg-[#EBF3FE]" : ""}`}
                  onClick={() => openDetail(p)}
                  data-testid={`dividend-plan-${p.id}`}
                >
                  <td className="py-2.5 px-4 text-sm font-semibold text-slate-800">
                    {p.source_fiscal_year} result → paid {p.payment_year}
                  </td>
                  <td className="py-2.5 px-4 text-right text-sm text-slate-700">{fmtSEK(p.computed?.result_basis_amount)} kr</td>
                  <td className="py-2.5 px-4 text-right text-sm text-slate-700">{p.payout_percentage} %</td>
                  <td className="py-2.5 px-4 text-right text-sm font-bold text-slate-900">{fmtSEK(p.computed?.planned_total_dividend)} kr</td>
                  <td className="py-2.5 px-4"><StatusBadge status={p.status} testId={`dividend-status-${p.id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && c && (
        <div className="space-y-6" data-testid="dividend-detail">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-slate-800">
                  {selected.source_fiscal_year} result → paid {selected.payment_year}
                </h2>
                <StatusBadge status={selected.status} testId="dividend-detail-status" />
              </div>
              {canEdit && (
                <div className="flex items-center gap-2">
                  {selected.status === "DRAFT" && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditForm({
                            payout_percentage: String(selected.payout_percentage),
                            dividend_decision_date: selected.dividend_decision_date || "",
                            notes: selected.notes || "",
                            override_amount: selected.override_amount != null ? String(selected.override_amount) : "",
                            override_comment: selected.override_comment || "",
                          });
                          setEditOpen(true);
                        }}
                        data-testid="dividend-edit-button"
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit plan
                      </Button>
                      <Button size="sm" className="bg-[#0052CC] hover:bg-[#0047B3]" onClick={() => setConfirmAction("activate")} data-testid="dividend-activate-button">
                        Activate
                      </Button>
                    </>
                  )}
                  {(selected.status === "DRAFT" || selected.status === "ACTIVE") && (
                    <Button variant="outline" size="sm" className="text-[#B91C1C] border-red-200 hover:bg-red-50" onClick={() => setConfirmAction("cancel")} data-testid="dividend-cancel-button">
                      Cancel plan
                    </Button>
                  )}
                  {selected.status === "CANCELLED" && (
                    <Button variant="outline" size="sm" onClick={() => setConfirmAction("reopen")} data-testid="dividend-reopen-button">
                      Reopen as Draft
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dividend-summary">
              <SummaryCell label={`Result basis (${selected.source_fiscal_year} PAT)`} value={`${fmtSEK(c.result_basis_amount)} kr`} testId="dividend-basis-amount" />
              <SummaryCell label="Payout %" value={`${c.payout_percentage} %`} testId="dividend-payout" />
              <SummaryCell label="Calculated dividend" value={`${fmtSEK(c.calculated_dividend)} kr`} testId="dividend-calculated" />
              <SummaryCell label="Override" value={c.override_amount != null ? `${fmtSEK(c.override_amount)} kr` : "—"} testId="dividend-override" />
              <SummaryCell label="Planned dividend" value={`${fmtSEK(c.planned_total_dividend)} kr`} testId="dividend-planned" strong />
              <SummaryCell label="Installments total" value={`${fmtSEK(c.installments_total)} kr`} testId="dividend-installments-total" />
              <SummaryCell label="Remaining to schedule" value={c.remaining_to_schedule == null ? "—" : `${fmtSEK(c.remaining_to_schedule)} kr`} testId="dividend-remaining" />
              <SummaryCell label="Decision date" value={selected.dividend_decision_date || "Not set"} testId="dividend-decision-date" />
            </div>

            <div className="mt-4 border border-slate-100 rounded-lg p-3 text-sm" data-testid="dividend-explain">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Why is the dividend {fmtSEK(c.planned_total_dividend)} kr?</p>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-slate-600">{selected.source_fiscal_year} Profit After Tax</span><span className="font-semibold">{fmtSEK(c.result_basis_amount)} kr</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Payout percentage</span><span className="font-semibold">{c.payout_percentage} %</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Calculated dividend</span><span className="font-semibold">{fmtSEK(c.calculated_dividend)} kr</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Override {selected.override_comment ? `(${selected.override_comment})` : ""}</span><span className="font-semibold">{c.override_amount != null ? `${fmtSEK(c.override_amount)} kr` : "—"}</span></div>
                <div className="flex justify-between border-t border-slate-100 pt-1"><span className="font-semibold text-slate-900">Planned dividend</span><span className="font-bold text-[#0052CC]">{fmtSEK(c.planned_total_dividend)} kr</span></div>
              </div>
            </div>

            {c.warnings?.length > 0 && (
              <div className="mt-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3" data-testid="dividend-warnings">
                {c.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-[#92400E] py-0.5" data-testid={`dividend-warning-${i}`}>
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" /> {w}
                  </div>
                ))}
              </div>
            )}

            {selected.status === "ACTIVE" && c.variance && (
              <div className="mt-4 border border-[#CFE3FC] bg-[#F8FAFC] rounded-lg p-3 text-sm" data-testid="dividend-variance">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Activated decision vs current forecast (never silently rewritten)</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div>Basis at activation<br /><span className="font-bold text-slate-900">{fmtSEK(c.variance.original_basis_at_activation)} kr</span></div>
                  <div>Current result basis<br /><span className="font-bold text-slate-900">{fmtSEK(c.variance.current_result_basis_amount)} kr</span></div>
                  <div>Current calculated reference<br /><span className="font-bold text-slate-900">{fmtSEK(c.variance.current_calculated_dividend)} kr</span></div>
                  <div>Active planned dividend<br /><span className="font-bold text-[#0052CC]">{fmtSEK(c.variance.active_planned_dividend)} kr</span>{c.variance.variance != null && c.variance.variance !== 0 && <span className="text-[#B45309]"> (variance {fmtSEK(c.variance.variance)})</span>}</div>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="dividend-allocation">
              <h3 className="text-sm font-semibold text-slate-800 mb-1">Shareholder Allocation</h3>
              <p className="text-xs text-slate-500 mb-3">
                {selected.dividend_decision_date
                  ? `Ownership effective on the dividend decision date ${selected.dividend_decision_date}.`
                  : "Provisional — set a planned dividend decision date to resolve ownership."}
                {selected.status === "ACTIVE" && " Snapshot at activation; later ownership changes do not rewrite an active plan."}
              </p>
              {(selected.status === "ACTIVE" ? snap?.ownership_allocation : c.allocation)?.length > 0 ? (
                <table className="w-full" data-testid="dividend-allocation-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left text-xs font-semibold text-slate-500 py-1.5">Shareholder</th>
                      <th className="text-right text-xs font-semibold text-slate-500 py-1.5">Ownership</th>
                      <th className="text-right text-xs font-semibold text-slate-500 py-1.5">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.status === "ACTIVE" ? snap.ownership_allocation : c.allocation).map((a, i) => (
                      <tr key={i} className="border-b border-slate-100" data-testid={`dividend-allocation-${i}`}>
                        <td className="py-1.5 text-sm text-slate-800">{a.shareholder_name}</td>
                        <td className="py-1.5 text-right text-sm text-slate-700">{a.ownership_percentage} %</td>
                        <td className="py-1.5 text-right text-sm font-semibold text-slate-900">{fmtSEK(a.amount)} kr</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-slate-500" data-testid="dividend-allocation-empty">No allocation — ownership unresolved.</p>
              )}
              {c.ownership && !c.ownership.complete && (
                <p className="text-xs text-[#B45309] mt-2" data-testid="dividend-ownership-incomplete">
                  Ownership totals {c.ownership.total_percentage}% — must total exactly 100% before activation.
                </p>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="dividend-schedule">
              <h3 className="text-sm font-semibold text-slate-800 mb-1">Payment Schedule — {selected.payment_year}</h3>
              <p className="text-xs text-slate-500 mb-3">Installments are cash-flow timing only. ACTIVE installments feed the official Cash Flow forecast (open periods only).</p>
              <div className="space-y-2" data-testid="dividend-installments">
                {instDraft.length === 0 && <p className="text-sm text-slate-500" data-testid="dividend-installments-empty">No installments scheduled.</p>}
                {instDraft.map((inst, idx) => (
                  <div key={idx} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2" data-testid={`dividend-installment-${idx}`}>
                    <span className="text-sm text-slate-800">{MONTHS[inst.payment_month - 1]} {inst.payment_year}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">{fmtSEK(inst.amount)} kr</span>
                      {editableSchedule && (
                        <button type="button" aria-label="Remove installment" className="p-1 rounded text-slate-400 hover:text-[#B91C1C] hover:bg-red-50" onClick={() => setInstDraft(instDraft.filter((_, i) => i !== idx))} data-testid={`dividend-installment-remove-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {editableSchedule && (
                <div className="mt-3 flex items-end gap-2">
                  <div className="space-y-1">
                    <Label>Month</Label>
                    <Select value={instMonth} onValueChange={setInstMonth}>
                      <SelectTrigger className="w-28" data-testid="dividend-installment-month"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((label, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)} data-testid={`dividend-installment-month-${i + 1}`}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Amount (kr)</Label>
                    <Input type="number" min="0" step="0.01" value={instAmount} onChange={(e) => setInstAmount(e.target.value)} data-testid="dividend-installment-amount" />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const amt = Number(instAmount);
                      if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
                      setInstDraft([...instDraft.filter((i) => i.payment_month !== Number(instMonth)), { payment_year: selected.payment_year, payment_month: Number(instMonth), amount: amt }].sort((a, b) => a.payment_month - b.payment_month));
                      setInstAmount("");
                    }}
                    data-testid="dividend-installment-add"
                  >
                    Add
                  </Button>
                  <Button className="bg-[#0052CC] hover:bg-[#0047B3]" onClick={saveInstallments} disabled={busy} data-testid="dividend-installments-save">
                    Save schedule
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="dividend-audit">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Audit history</h3>
            <div className="space-y-1">
              {(selected.audit_log || []).slice().reverse().map((a, i) => (
                <div key={i} className="text-xs text-slate-600 flex gap-2" data-testid={`dividend-audit-${i}`}>
                  <span className="text-slate-400 shrink-0">{fmtDate(a.at)}</span>
                  <span className="font-semibold">{a.event}</span>
                  {a.comment && <span className="italic">— {a.comment}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="dividend-create-dialog">
          <DialogHeader>
            <DialogTitle>New dividend plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Source fiscal year (result)</Label>
                <Select value={createForm.source_fiscal_year} onValueChange={(v) => setCreateForm({ ...createForm, source_fiscal_year: v, payment_year: String(Number(v) + 1) })}>
                  <SelectTrigger data-testid="dividend-create-source-year"><SelectValue placeholder="Year" /></SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)} data-testid={`dividend-create-source-${y}`}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Payment year</Label>
                <Input type="number" value={createForm.payment_year} onChange={(e) => setCreateForm({ ...createForm, payment_year: e.target.value })} data-testid="dividend-create-payment-year" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Payout percentage</Label>
                <Input type="number" min="0" max="100" step="0.01" value={createForm.payout_percentage} onChange={(e) => setCreateForm({ ...createForm, payout_percentage: e.target.value })} data-testid="dividend-create-payout" />
              </div>
              <div className="space-y-1.5">
                <Label>Dividend decision date (planned)</Label>
                <Input type="date" value={createForm.dividend_decision_date} onChange={(e) => setCreateForm({ ...createForm, dividend_decision_date: e.target.value })} data-testid="dividend-create-decision-date" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} data-testid="dividend-create-notes" />
            </div>
            <p className="text-xs text-slate-500">Result basis: Profit After Tax (canonical company P&L). One plan per company, source fiscal year and payment year.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="dividend-create-cancel">Cancel</Button>
            <Button onClick={createPlan} disabled={busy || !createForm.source_fiscal_year || !createForm.payment_year} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="dividend-create-save">Create draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="dividend-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit dividend plan (Draft)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Payout percentage</Label>
                <Input type="number" min="0" max="100" step="0.01" value={editForm.payout_percentage} onChange={(e) => setEditForm({ ...editForm, payout_percentage: e.target.value })} data-testid="dividend-edit-payout" />
              </div>
              <div className="space-y-1.5">
                <Label>Dividend decision date</Label>
                <Input type="date" value={editForm.dividend_decision_date} onChange={(e) => setEditForm({ ...editForm, dividend_decision_date: e.target.value })} data-testid="dividend-edit-decision-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Override planned dividend (kr)</Label>
                <Input type="number" min="0" step="0.01" value={editForm.override_amount} onChange={(e) => setEditForm({ ...editForm, override_amount: e.target.value })} placeholder="Empty = use calculated" data-testid="dividend-edit-override" />
              </div>
              <div className="space-y-1.5">
                <Label>Override comment</Label>
                <Input value={editForm.override_comment} onChange={(e) => setEditForm({ ...editForm, override_comment: e.target.value })} data-testid="dividend-edit-override-comment" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="dividend-edit-notes" />
            </div>
            <p className="text-xs text-slate-500">An override retains the calculated amount underneath — the original value is never destroyed.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="dividend-edit-cancel">Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="dividend-edit-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmAction} onOpenChange={(v) => !v && setConfirmAction(null)}>
        <DialogContent className="max-w-sm" data-testid="dividend-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirmAction === "activate" ? "Activate dividend plan?" : confirmAction === "cancel" ? "Cancel dividend plan?" : "Reopen as Draft?"}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="text-sm text-slate-600 space-y-1" data-testid="dividend-confirm-summary">
              <p className="font-semibold text-slate-900">{selected.source_fiscal_year} result → paid {selected.payment_year}</p>
              <p>Planned dividend: {fmtSEK(c?.planned_total_dividend)} kr · {(selected.installments || []).length} installment(s)</p>
              {confirmAction === "activate" && (
                <p className="pt-1">Activation snapshots basis, payout, planned dividend, ownership allocation and schedule. ACTIVE installments feed the official Cash Flow forecast. The decision is never silently rewritten afterwards.</p>
              )}
              {confirmAction === "cancel" && (
                <>
                  <p className="pt-1">The plan stops feeding Cash Flow. History remains.</p>
                  <div className="space-y-1.5 pt-2">
                    <Label>Comment / reason</Label>
                    <Input value={cancelComment} onChange={(e) => setCancelComment(e.target.value)} data-testid="dividend-cancel-comment" />
                  </div>
                </>
              )}
              {confirmAction === "reopen" && <p className="pt-1">The plan returns to DRAFT and can be edited and re-activated.</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)} data-testid="dividend-confirm-cancel">Back</Button>
            <Button
              onClick={runAction}
              disabled={busy || (confirmAction === "cancel" && !cancelComment.trim())}
              className={confirmAction === "cancel" ? "bg-[#B91C1C] hover:bg-[#991B1B]" : "bg-[#0052CC] hover:bg-[#0047B3]"}
              data-testid="dividend-confirm-go"
            >
              {confirmAction === "activate" ? "Activate" : confirmAction === "cancel" ? "Cancel plan" : "Reopen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
